import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Check, ArrowRight, ArrowLeft, Loader2, XCircle, Send, Clock, KeyRound, ExternalLink, ShieldCheck, Info, Users, Eye, Settings2, ShieldAlert, AlertTriangle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Badge, Input } from "@/components/kit";
import UpgradeGate from "@/components/UpgradeGate";
import BlastActivityFeed from "@/components/BlastActivityFeed";
import LeadPicker from "@/components/LeadPicker";

function SmtpTutorial() {
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-5" data-testid="smtp-tutorial">
      <div className="flex items-center gap-2 mb-3">
        <Info className="w-5 h-5 text-sky-600" />
        <h4 className="font-display font-bold text-slate-800">Cara membuat Gmail App Password</h4>
      </div>
      <p className="text-sm text-slate-600 mb-3">
        App Password adalah sandi 16 karakter khusus aplikasi. Bukan password Gmail biasa.
        Wajib mengaktifkan <b>Verifikasi 2 Langkah</b> terlebih dahulu.
      </p>
      <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
        <li>Buka <b>Akun Google</b> kamu → menu <b>Security / Keamanan</b>.</li>
        <li>Aktifkan <b>2-Step Verification</b> (Verifikasi 2 Langkah) bila belum aktif.</li>
        <li>Buka halaman <b>App passwords</b> (App Password / Sandi Aplikasi).</li>
        <li>Pada "App name", ketik <b>RESADA</b> lalu klik <b>Create / Buat</b>.</li>
        <li>Google menampilkan sandi <b>16 huruf</b>. Salin sandi itu.</li>
        <li>Tempel ke kolom <b>App Password</b> di bawah, lalu klik <b>Hubungkan</b>.</li>
      </ol>
      <div className="flex flex-wrap gap-2 mt-4">
        <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
          data-testid="smtp-apppassword-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 bg-white border border-sky-200 rounded-lg px-3 py-1.5 hover:bg-sky-100 transition">
          <KeyRound className="w-4 h-4" /> Buka App Passwords <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-100 transition">
          <ShieldCheck className="w-4 h-4" /> Aktifkan 2FA <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

export default function EmailBlast() {
  const { quota, refreshQuota } = useAuth();
  const [step, setStep] = useState(1);
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [smtp, setSmtp] = useState(null);
  const [smtpEmail, setSmtpEmail] = useState("");
  const [smtpPwd, setSmtpPwd] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [schedule, setSchedule] = useState("");
  const [useSchedule, setUseSchedule] = useState(false);
  const [ignoreCooldown, setIgnoreCooldown] = useState(false);
  const [deliverability, setDeliverability] = useState(null);
  const [checkingDeliverability, setCheckingDeliverability] = useState(false);
  const pollRef = useRef(null);

  const loadSmtp = () => api.get("/smtp/status").then((r) => setSmtp(r.data)).catch(() => setSmtp({ connected: false }));

  useEffect(() => {
    api.get("/leads", { params: { per_page: 1000, has_email: true } }).then((r) => setLeads(r.data.leads)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data.templates.filter((t) => t.type === "email"))).catch(() => {});
    loadSmtp();
  }, []);

  const connectSmtp = async () => {
    if (!smtpEmail || !smtpPwd) { toast.error("Isi email & App Password dulu"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post("/smtp/connect", { email: smtpEmail.trim(), app_password: smtpPwd });
      toast.success(`Gmail SMTP terhubung sebagai ${data.email}`);
      setSmtpPwd("");
      loadSmtp();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConnecting(false); }
  };
  const disconnectSmtp = async () => { await api.post("/smtp/disconnect"); toast.success("SMTP diputuskan"); loadSmtp(); };
  const testSmtp = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/smtp/test");
      toast.success(`Email tes terkirim ke ${data.email}. Cek inbox kamu.`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTesting(false); }
  };

  const checkDeliverability = async () => {
    if (!selectedTemplate) { toast.error("Pilih template dulu"); return; }
    const { subject, body } = renderPreview(selectedTemplate, selectedLeads[0]);
    setCheckingDeliverability(true);
    try {
      const { data } = await api.post("/deliverability/check", {
        from_email: smtp?.email || "",
        subject,
        body_html: `<div style="font-family:Arial,sans-serif;font-size:15px;color:#0f172a;line-height:1.6">${body.replace(/\n/g, "<br/>")}</div>`,
      });
      setDeliverability(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCheckingDeliverability(false); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedLeads = leads.filter((l) => selected[l.id]);
  const selectedTemplate = templates.find((t) => t.id === templateId);

  const wsRef = useRef(null);

  const connectWs = useCallback((id) => {
    const base = (api.defaults.baseURL || "").replace(/\/api$/, "").replace(/^http/, "ws");
    const token = localStorage.getItem("__session") || "";
    const ws = new WebSocket(`${base}/ws/blast/${id}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "progress") {
          setJob({
            status: msg.status, sent: msg.sent, failed: msg.failed,
            skipped: msg.skipped, progress_pct: msg.progress_pct, total_leads: msg.total,
          });
          if (["done", "cancelled", "failed"].includes(msg.status)) {
            ws.close(); refreshQuota();
          }
        } else if (msg.type === "complete") {
          ws.close(); refreshQuota();
        }
      } catch {}
    };
    ws.onerror = () => { ws.close(); pollFallback(id); };
    ws.onclose = () => { wsRef.current = null; };
  }, [refreshQuota]);

  const pollFallback = useCallback((id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/blast/job/${id}`);
        setJob(data);
        if (["done", "cancelled", "failed"].includes(data.status)) { clearInterval(pollRef.current); refreshQuota(); }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
  }, [refreshQuota]);

  useEffect(() => () => { clearInterval(pollRef.current); if (wsRef.current) wsRef.current.close(); }, []);

  const start = async () => {
    if (useSchedule && !schedule) { toast.error("Pilih tanggal & jam dulu"); return; }
    const scheduled_at = useSchedule && schedule ? new Date(schedule).toISOString() : null;
    setLoading(true);
    try {
      const { data } = await api.post("/blast/email", { lead_ids: selectedIds, template_id: templateId, scheduled_at, ignore_cooldown: ignoreCooldown });
      if (data.scheduled) {
        toast.success(`Email blast dijadwalkan untuk ${new Date(schedule).toLocaleString("id-ID")}`);
        setStep(1); setSelected({}); setTemplateId(""); setUseSchedule(false); setSchedule("");
      } else {
        toast.success("Email blast dimulai!");
        setStep(3); setJobId(data.job_id);
        setJob({ status: "queued", sent: 0, failed: 0, skipped: 0, total_leads: data.total, progress_pct: 0 });
        connectWs(data.job_id);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const cancel = async () => { if (jobId) { try { await api.post(`/blast/job/${jobId}/cancel`); toast.success("Membatalkan..."); } catch {} } };

  // Substitute variables in template preview; mirrors backend r_blast.render_template().
  const renderPreview = (tpl, lead) => {
    if (!tpl) return { subject: "", body: "" };
    let body = tpl.content || ""; let subject = tpl.subject || "";
    if (!lead) return { subject, body };
    const vars = {
      "{nama_bisnis}": lead.business_name || "",
      "{kota}": lead.city || "",
      "{kategori}": lead.category || "",
      "{website}": lead.website || "",
      "{rating}": lead.rating != null ? String(lead.rating) : "",
      "{nama_user}": "kamu",
    };
    Object.entries(vars).forEach(([k, v]) => {
      body = body.split(k).join(v);
      subject = subject.split(k).join(v);
    });
    return { subject, body };
  };

  const canBlast = (quota?.blast_email?.limit ?? 0) !== 0;
  const Steps = [
    { label: "Pilih Lead", icon: Users },
    { label: "Template", icon: Eye },
    { label: "Kirim", icon: Send },
  ];

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-violet-100 grid place-items-center shrink-0"><Mail className="w-5 h-5 text-violet-600" /></span>
            <h1 className="font-display text-3xl font-extrabold">Email Blast</h1>
          </span>
          <p className="text-slate-500 mt-0.5 text-sm">Kirim email massal lewat Gmail SMTP (App Password) akun kamu sendiri.</p>
        </div>
        {smtp?.connected && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-emerald-800"><b>{smtp.email}</b> · Gmail SMTP</span>
            <Button data-testid="smtp-test-btn" size="sm" variant="outline" loading={testing} onClick={testSmtp}><Send className="w-3.5 h-3.5" /> Test</Button>
            <button onClick={disconnectSmtp} className="text-xs text-rose-500 font-semibold ml-1" data-testid="smtp-disconnect">Putuskan</button>
          </div>
        )}
      </div>

      <UpgradeGate active={!canBlast} feature="Email Blast">
        {smtp && !smtp.connected ? (
          <Card className="p-6 space-y-5" data-testid="smtp-connect-card">
            <div>
              <h3 className="font-display text-xl font-bold">Hubungkan Gmail SMTP</h3>
              <p className="text-sm text-slate-500 mt-1">Email blast dikirim langsung dari akun Gmail kamu menggunakan App Password.</p>
            </div>
            <SmtpTutorial />
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Email Gmail</label>
                <Input data-testid="smtp-email-input" type="email" placeholder="kamu@gmail.com" value={smtpEmail} onChange={(e) => setSmtpEmail(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">App Password (16 huruf)</label>
                <Input data-testid="smtp-password-input" type="password" placeholder="abcd efgh ijkl mnop" value={smtpPwd} onChange={(e) => setSmtpPwd(e.target.value)} />
              </div>
            </div>
            <Button data-testid="smtp-connect-btn" loading={connecting} onClick={connectSmtp}><KeyRound className="w-4 h-4" /> Hubungkan</Button>
          </Card>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {Steps.map((s, i) => (
                <div key={s.label} className="flex items-center gap-2 flex-1">
                  <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold transition ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-violet-500 text-white ring-4 ring-violet-100" : "bg-slate-100 text-slate-400"}`}>
                    {step > i + 1 ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm hidden sm:block ${step === i + 1 ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
                  {i < Steps.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div className="space-y-4">
                <LeadPicker leads={leads} setLeads={setLeads} selected={selected} setSelected={setSelected} channel="email" accent="violet" />
                <div className="flex justify-between items-center sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-lg">
                  <div className="text-sm"><b className="text-violet-600">{selectedIds.length}</b> lead dipilih</div>
                  <Button data-testid="email-next-1" disabled={selectedIds.length === 0} onClick={() => setStep(2)} size="lg">
                    Lanjut ke Template <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid lg:grid-cols-3 gap-4">
                <Card className="p-5 lg:col-span-2">
                  <h3 className="font-display font-bold mb-4">Pilih Template Email</h3>
                  {templates.length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">Belum ada template email. Buat template bertipe "email" di halaman Template.</p>
                  ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                      {templates.map((t) => (
                        <label key={t.id} className={`block p-4 rounded-xl border cursor-pointer transition ${templateId === t.id ? "border-violet-400 bg-violet-50 ring-2 ring-violet-100" : "border-slate-200 hover:border-violet-200"}`} data-testid="email-template-option">
                          <div className="flex items-center gap-2">
                            <input type="radio" name="etpl" checked={templateId === t.id} onChange={() => setTemplateId(t.id)} className="accent-violet-500" />
                            <span className="font-semibold text-slate-800">{t.name}</span>
                          </div>
                          {t.subject && <p className="text-xs text-violet-600 mt-1">Subjek: {t.subject}</p>}
                          <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap line-clamp-3">{t.content}</p>
                        </label>
                      ))}
                    </div>
                  )}
                  <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-amber-200 bg-amber-50 p-4 mt-4">
                    <input type="checkbox" checked={ignoreCooldown} onChange={(e) => setIgnoreCooldown(e.target.checked)} data-testid="email-ignore-cooldown" className="mt-0.5" />
                    <span className="text-sm text-amber-800">
                      <b>Abaikan cooldown 7 hari</b> — tetap kirim ke semua email terpilih meski sudah pernah dikirim sebelumnya.
                      <span className="block text-xs text-amber-600 mt-0.5">Gunakan dengan bijak agar tidak dianggap spam oleh penyedia email.</span>
                    </span>
                  </label>
                  <div className="rounded-xl border border-slate-200 p-4 mt-3">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={useSchedule} onChange={(e) => setUseSchedule(e.target.checked)} data-testid="email-schedule-toggle" />
                      <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Clock className="w-4 h-4 text-violet-500" /> Jadwalkan untuk nanti</span>
                    </label>
                    {useSchedule && (
                      <input type="datetime-local" value={schedule} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                        onChange={(e) => setSchedule(e.target.value)} data-testid="email-schedule-input"
                        className="w-full mt-3 rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
                    )}
                  </div>
                </Card>
                <Card className="p-5 sticky top-4 self-start">
                  <h4 className="font-display font-bold text-sm text-slate-600 mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-violet-500" /> Pratinjau Email</h4>
                  {selectedTemplate ? (() => {
                    const { subject, body } = renderPreview(selectedTemplate, selectedLeads[0]);
                    return (
                      <div className="space-y-2">
                        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">
                          Ke: <b className="text-slate-700">{selectedLeads[0]?.email || "—"}</b><br />
                          Subjek: <b className="text-slate-700">{subject || "(tanpa subjek)"}</b>
                        </div>
                        <div className="rounded-lg bg-violet-50 border border-violet-200 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {body}
                        </div>
                        <button
                          onClick={checkDeliverability}
                          disabled={checkingDeliverability}
                          data-testid="deliverability-check-btn"
                          className="w-full flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition disabled:opacity-60"
                        >
                          {checkingDeliverability ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                          {checkingDeliverability ? "Mengecek..." : "Cek Deliverability"}
                        </button>
                      </div>
                    );
                  })() : (
                    <p className="text-xs text-slate-400">Pilih template untuk melihat pratinjau berdasarkan lead pertama.</p>
                  )}
                  <div className="mt-3 rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-700">
                    <b>{selectedIds.length}</b> email akan dikirim · cooldown 7 hari {ignoreCooldown ? "diabaikan" : "aktif"}.
                  </div>
                </Card>
                <div className="lg:col-span-3 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4" /> Kembali</Button>
                  <Button data-testid="email-start" loading={loading} disabled={!templateId} onClick={start} size="lg">
                    {useSchedule ? <><Clock className="w-4 h-4" /> Jadwalkan</> : <><Send className="w-4 h-4" /> Kirim Email ({selectedIds.length})</>}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && job && (
              <Card className="p-6" data-testid="email-progress">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold flex items-center gap-2">
                    {["done", "cancelled", "failed"].includes(job.status) ? <Check className="w-5 h-5 text-emerald-500" /> : <Loader2 className="w-5 h-5 text-violet-500 animate-spin" />}
                    Email Blast {job.status}
                  </h3>
                  <Badge color="violet">{job.progress_pct}%</Badge>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full transition-all duration-500" style={{ width: `${job.progress_pct}%` }} />
                </div>
                <div className="grid grid-cols-4 gap-3 mt-5 text-center">
                  {[["Total", job.total_leads], ["Terkirim", job.sent], ["Gagal", job.failed], ["Skip", job.skipped]].map(([l, v]) => (
                    <div key={l} className="bg-slate-50 rounded-xl py-3">
                      <p className="font-display text-2xl font-extrabold">{v}</p>
                      <p className="text-xs text-slate-500">{l}</p>
                    </div>
                  ))}
                </div>
                <BlastActivityFeed jobId={jobId} leads={leads} running={!["done", "cancelled", "failed"].includes(job.status)} channel="email" accent="violet" />
                <div className="flex justify-between mt-5">
                  {!["done", "cancelled", "failed"].includes(job.status) ?
                    <Button variant="danger" size="sm" onClick={cancel}><XCircle className="w-4 h-4" /> Batalkan</Button> : <div />}
                  <Button variant="outline" onClick={() => { setStep(1); setJob(null); setSelected({}); setTemplateId(""); }}>Blast Baru</Button>
                </div>
              </Card>
            )}
          </>
        )}
      </UpgradeGate>

      {/* Deliverability Report Modal */}
      {deliverability && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDeliverability(null)} />
          <Card className="relative w-full max-w-lg p-6 animate-float-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" /> Deliverability Report
              </h2>
              <button onClick={() => setDeliverability(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            {/* Spam Score */}
            <div className={`rounded-xl p-4 mb-4 ${deliverability.spam.level === "good" ? "bg-emerald-50 border border-emerald-200" : deliverability.spam.level === "fair" ? "bg-sky-50 border border-sky-200" : deliverability.spam.level === "warning" ? "bg-amber-50 border border-amber-200" : "bg-rose-50 border border-rose-200"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">Spam Score</span>
                <span className={`font-display text-2xl font-extrabold ${deliverability.spam.level === "good" ? "text-emerald-600" : deliverability.spam.level === "fair" ? "text-sky-600" : deliverability.spam.level === "warning" ? "text-amber-600" : "text-rose-600"}`}>
                  {deliverability.spam.score}/100
                </span>
              </div>
              <Badge color={deliverability.spam.level === "good" ? "green" : deliverability.spam.level === "fair" ? "sky" : deliverability.spam.level === "warning" ? "amber" : "red"}>
                {deliverability.spam.label}
              </Badge>
            </div>

            {/* Findings */}
            {deliverability.spam.findings.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">Temuan:</p>
                <div className="space-y-1.5">
                  {deliverability.spam.findings.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {deliverability.links.broken > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">Link bermasalah ({deliverability.links.broken}):</p>
                {deliverability.links.details.filter((l) => l.issues.length > 0).map((l, i) => (
                  <div key={i} className="text-xs text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mb-1">
                    <p className="font-mono truncate">{l.url}</p>
                    <p className="text-rose-500">{l.issues.join(", ")}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div className="bg-slate-50 rounded-lg py-2">
                <p className="font-bold">{deliverability.stats.word_count}</p>
                <p className="text-[11px] text-slate-400">kata</p>
              </div>
              <div className="bg-slate-50 rounded-lg py-2">
                <p className="font-bold">{deliverability.links.total}</p>
                <p className="text-[11px] text-slate-400">link</p>
              </div>
              <div className="bg-slate-50 rounded-lg py-2">
                <p className="font-bold">{deliverability.stats.image_count}</p>
                <p className="text-[11px] text-slate-400">gambar</p>
              </div>
            </div>

            {/* Recommendation */}
            <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700">
              <b>Rekomendasi:</b> {deliverability.recommendation}
            </div>

            <Button variant="outline" className="w-full mt-4" onClick={() => setDeliverability(null)}>Tutup</Button>
          </Card>
        </div>
      )}
    </div>
  );
}
